import React from 'react';
import { Phone, Mail, MapPin, MessageCircle, ShieldCheck, Scale, Award, Cookie } from 'lucide-react';
import { useProperties } from '../context/PropertyContext';
import { openCookiePreferencesModal } from '../services/cookieConsentService';

export const Footer = ({ onNavigate }) => {
  const { getWhatsAppLink } = useProperties();

  const handleLinkClick = (e, routeId) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(routeId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer" role="contentinfo">
      <div className="container">
        <div className="footer-grid">
          {/* Column 1: Brand & Professional Profile */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#ffffff', padding: '6px 12px', borderRadius: 'var(--radius-md)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>
                <img
                  src="/EM Asesorias.svg"
                  alt="EM Asesoría Inmobiliaria y Jurídica - Lic. Edgar Molina"
                  width="48"
                  height="48"
                  style={{ height: '48px', width: '48px' }}
                />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-white)' }}>
                  EM <span style={{ color: 'var(--color-accent)' }}>Asesoría</span>
                </div>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', letterSpacing: '0.05em' }}>
                  Inmobiliaria & Jurídica
                </div>
              </div>
            </div>

            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '16px' }}>
              Firma inmobiliaria y legal liderada por el <strong>Lic. Edgar Molina</strong> en Hidalgo del Parral, Chihuahua. Especialistas en compraventa, arrendamiento seguro, avalúos certificados y certeza jurídica en transmisión patrimonial.
            </p>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <a
                href="https://www.facebook.com/profile.php?id=61578869614923"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-white btn-sm"
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                aria-label="Facebook oficial de EM Asesoría"
              >
                Facebook
              </a>
              <a
                href="https://www.instagram.com/emasesoria/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-white btn-sm"
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                aria-label="Instagram oficial de EM Asesoría"
              >
                Instagram
              </a>
            </div>
          </div>

          {/* Column 2: Navigation Links */}
          <div>
            <h4 className="footer-heading">Navegación</h4>
            <ul className="footer-links">
              <li>
                <a href="#home" onClick={(e) => handleLinkClick(e, 'home')}>
                  Inicio
                </a>
              </li>
              <li>
                <a href="#properties" onClick={(e) => handleLinkClick(e, 'properties')}>
                  Catálogo de Inmuebles
                </a>
              </li>
              <li>
                <a href="#sell" onClick={(e) => handleLinkClick(e, 'sell')}>
                  Vende o Renta tu Propiedad
                </a>
              </li>
              <li>
                <a href="#guide" onClick={(e) => handleLinkClick(e, 'guide')}>
                  Guía Inmobiliaria & FAQ
                </a>
              </li>
              <li>
                <a href="#about" onClick={(e) => handleLinkClick(e, 'about')}>
                  Perfil Lic. Edgar Molina
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal & Regulatory Links */}
          <div>
            <h4 className="footer-heading">Marco Legal & Normativa</h4>
            <ul className="footer-links">
              <li>
                <a href="#aviso-de-privacidad" onClick={(e) => handleLinkClick(e, 'privacy')}>
                  Aviso de Privacidad Integral
                </a>
              </li>
              <li>
                <a href="#terminos-y-condiciones" onClick={(e) => handleLinkClick(e, 'terms')}>
                  Términos y Condiciones (NOM-247)
                </a>
              </li>
              <li>
                <a href="#politica-de-cookies" onClick={(e) => handleLinkClick(e, 'cookies')}>
                  Política de Cookies
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookiePreferencesModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--color-accent)',
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    textAlign: 'left'
                  }}
                >
                  <Cookie size={14} />
                  <span>Configurar Preferencias de Cookies</span>
                </button>
              </li>
              <li style={{ marginTop: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem', display: 'block', lineHeight: '1.4' }}>
                  Precios en MXN · Cumplimiento NOM-247-SE-2021 y LFPDPPP
                </span>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact Information in Parral */}
          <div>
            <h4 className="footer-heading">Contacto Directo</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <MapPin size={18} color="var(--color-accent)" style={{ flexShrink: 0, marginTop: '3px' }} />
                <span>Hidalgo del Parral, Chihuahua, México</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Phone size={18} color="var(--color-accent)" style={{ flexShrink: 0 }} />
                <a href="tel:6272792964" style={{ color: 'var(--color-white)', fontWeight: 600 }}>
                  627 279 2964
                </a>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <MessageCircle size={18} color="var(--color-accent)" style={{ flexShrink: 0 }} />
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-accent)', fontWeight: 700 }}
                >
                  WhatsApp: 627 279 2964
                </a>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Mail size={18} color="var(--color-accent)" style={{ flexShrink: 0 }} />
                <a href="mailto:juridico@asesoriasem.com" style={{ color: 'var(--color-white)', textDecoration: 'none', fontWeight: 600 }}>
                  juridico@asesoriasem.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div>
            © 2026 <strong>EM Asesoría Inmobiliaria y Jurídica</strong>. Todos los derechos reservados. Lic. Edgar Molina.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span>Hidalgo del Parral, Chih.</span>
            <span>·</span>
            <a
              href="#aviso-de-privacidad"
              onClick={(e) => handleLinkClick(e, 'privacy')}
              style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
            >
              Aviso de Privacidad
            </a>
            <span>·</span>
            <a
              href="#terminos-y-condiciones"
              onClick={(e) => handleLinkClick(e, 'terms')}
              style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
            >
              Términos y Condiciones
            </a>
            <span>·</span>
            <a
              href="#politica-de-cookies"
              onClick={(e) => handleLinkClick(e, 'cookies')}
              style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none' }}
            >
              Cookies
            </a>
            <span>·</span>
            <span>
              Desarrollado por{' '}
              <a
                href="https://caoslab.dev"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--color-accent)', fontWeight: 600, textDecoration: 'none' }}
              >
                caoslab.dev
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
